import React, { useEffect, useRef, useState } from 'react'
import { Icon } from '@chakra-ui/icons'
import { FaXmark, FaChevronLeft, FaChevronRight } from 'react-icons/fa6'
import { Avatar } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { Text, Box, BoxProps } from '@chakra-ui/react'

import { useChat } from '../../chat-context'
import { getAlivePatients } from '~utils/helpers'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { ClientPreviewFieldsFragment } from '~graphql/generated/graphql'

interface Props {
    showClientInfo: boolean
}

export default function PatientTabs({ showClientInfo }: Props) {
    const { chat, patientFilter, setPatientFilter } = useChat()

    // Ref for the horizontal scroll container
    const scrollRef = useRef<HTMLDivElement>(null)

    // Track scroll position to decide when to show arrows
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const sortedPatients = [...getAlivePatients(chat.client.patients)].sort(
        // sort by is_deceased then name
        (a, b) => {
            return a.name?.localeCompare(b.name || '') || 0
        },
    )

    const handlePatientClick = (patientId: string) => {
        const isActive = patientFilter.includes(patientId)
        if (isActive) {
            setPatientFilter(patientFilter.filter(pId => pId !== patientId))
        } else {
            setPatientFilter([...patientFilter, patientId])
        }
    }

    const updateScrollIndicators = () => {
        const el = scrollRef.current
        if (!el) return
        setCanScrollLeft(el.scrollLeft > 0)
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1) // -1 to account for subpixel widths
    }

    const scrollBy = (delta: number) => {
        scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' })
    }

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        // Initialize indicator visibility
        updateScrollIndicators()

        const observer = new ResizeObserver(updateScrollIndicators)
        observer.observe(el)

        el.addEventListener('scroll', updateScrollIndicators)
        window.addEventListener('resize', updateScrollIndicators)

        return () => {
            observer.disconnect()
            el.removeEventListener('scroll', updateScrollIndicators)
            window.removeEventListener('resize', updateScrollIndicators)
        }
    }, [])

    if (sortedPatients.length < 1) return null

    return (
        <SharedStack
            row
            py={2}
            gap={2}
            overflow="hidden"
            maxW={{
                base: 'none',
                lg: showClientInfo ? '814px' : '1174px',
            }}
        >
            <Box pl={{ base: 4, lg: 6 }}>
                <Text fontSize="xs" color="gray.500">
                    Patients:
                </Text>
            </Box>
            {/* Scrollable patient list with overlay arrows */}
            <Box position="relative" flex="1" overflow="hidden">
                <Box
                    ref={scrollRef}
                    overflowX="auto"
                    __css={{
                        scrollBehavior: 'smooth',
                        '::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    <SharedStack row gap={2} w="max-content" pr={6}>
                        {sortedPatients.map(p => (
                            <PatientButton
                                key={p.id}
                                patient={p}
                                isActive={patientFilter.includes(p.id)}
                                onClick={() => handlePatientClick(p.id)}
                            />
                        ))}
                    </SharedStack>
                </Box>

                {/* Left scroll indicator */}
                {canScrollLeft && (
                    <ScrollArrow
                        direction="left"
                        onClick={() => scrollBy(-200)}
                    />
                )}

                {/* Right scroll indicator */}
                {canScrollRight && (
                    <ScrollArrow
                        direction="right"
                        onClick={() => scrollBy(200)}
                    />
                )}
            </Box>
        </SharedStack>
    )
}

interface PatientButtonProps {
    patient: NonNullable<ClientPreviewFieldsFragment['patients']>[0]
    isActive: boolean
    onClick: () => void
}

function PatientButton({ patient, isActive, onClick }: PatientButtonProps) {
    return (
        <Button
            key={patient.id}
            onClick={onClick}
            variant="subtle"
            colorScheme={isActive ? 'brand' : 'gray'}
            h="28px"
            size="xs"
            minW="min-content"
            borderWidth="1px"
            borderRadius="full"
            borderColor={isActive ? 'brand.500' : 'gray.200'}
        >
            <SharedStack row fit>
                <Avatar
                    size="xs"
                    name={patient?.name || 'Unknown'}
                    w="18px"
                    h="18px"
                    fontSize="13px"
                />
                <Text>
                    {patient.name}
                    {patient.is_deceased ? ' (Deceased)' : ''}
                </Text>
                {isActive && <Icon as={FaXmark} />}
            </SharedStack>
        </Button>
    )
}

interface ScrollArrowProps {
    direction: 'left' | 'right'
    onClick: () => void
}

function ScrollArrow({ direction, onClick }: ScrollArrowProps) {
    const isLeft = direction === 'left'

    const arrowStyles: BoxProps = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        w: '50px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 1,
    }

    if (isLeft) {
        return (
            <Box
                {...arrowStyles}
                left={0}
                justifyContent="flex-start"
                bgGradient="linear(to-r, white 30%, transparent)"
                onClick={onClick}
            >
                <Icon as={FaChevronLeft} boxSize="16px" ml={2} />
            </Box>
        )
    }

    return (
        <Box
            {...arrowStyles}
            right={0}
            justifyContent="flex-end"
            bgGradient="linear(to-l, white 30%, transparent)"
            onClick={onClick}
        >
            <Icon as={FaChevronRight} boxSize="16px" mr={3} />
        </Box>
    )
}
